/**
 * Trailblazers team colour palette.
 *
 * Every TRL team is assigned exactly one of these colours, unique within its
 * game. The colour drives the admin team cards, the player overlays, the team's
 * trail and cleared planets on the galaxy map, and the team's Discord role
 * colour. Rendered as a 5x5 grid in the admin picker, read left-to-right.
 *
 * A single even sweep around the wheel — `hsl(240 + 12.5n, 86%, 57%)` for n =
 * 0..24 — running blue → violet → magenta → pink → red → orange → yellow →
 * green → spring → cyan. Fixed saturation and lightness are the point: every
 * entry carries the same visual weight, so no team gets a colour that reads
 * louder or fainter than another on the dark map and overlays.
 *
 * DELIBERATELY ABSENT: white/near-white and pale cyan-blue tints, which the
 * previous palette carried. White is the map's own neutral (uncharted planets,
 * default path strokes), and the pale blues sat too close to `$color-primary`
 * (the "you are here" planet) and `$color-cyan_light` (a frontier planet) to be
 * read as a team's identity.
 *
 * Changing this list does NOT rewrite stored colours: a team created under an
 * older palette keeps its hex and renders fine, it simply can't be re-picked
 * from the grid. Migration 043's backfill inlines its own frozen snapshot, so it
 * is unaffected by edits here.
 */
export const TRL_TEAM_COLORS = [
    '#3333f0', '#5a33f0', '#8233f0', '#a933f0', '#d033f0',
    '#f033e8', '#f033c0', '#f03399', '#f03372', '#f0334b',
    '#f04333', '#f06a33', '#f09133', '#f0b933', '#f0e033',
    '#d8f033', '#b1f033', '#89f033', '#62f033', '#3bf033',
    '#33f052', '#33f07a', '#33f0a1', '#33f0c8', '#33f0f0',
];
/** True when `color` is one of the palette entries (case-insensitive). */
export function isTrlTeamColor(color) {
    if (!color)
        return false;
    const lower = color.toLowerCase();
    return TRL_TEAM_COLORS.includes(lower);
}
/** First palette colour not present in `usedColors`, or undefined when all are taken. */
export function pickFirstFreeTrlTeamColor(usedColors) {
    const used = new Set();
    for (const c of usedColors) {
        if (c)
            used.add(c.toLowerCase());
    }
    return TRL_TEAM_COLORS.find((c) => !used.has(c));
}
